import React, { useContext } from 'react';
import { CloudUploadOutlined } from '@ant-design/icons';
import type { UploadRequestOption } from '@rc-component/upload/lib/interface';
import { Alert, Flex, Spin, Tag, Typography, Upload } from 'antd';

import SoftwareImportContext from '@totem/components/devices/software-import/SoftwareImportContext';
import {
  OPTIONAL_COLUMNS,
  REQUIRED_COLUMNS,
} from '@totem/components/devices/software-import/utilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const { Paragraph, Text } = Typography;

const SoftwareImportUpload = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { status, fileName, error, onImport } = useContext(
    SoftwareImportContext,
  );
  const busy = status === 'validating' || status === 'uploading';

  const handleUpload = ({ file, onSuccess }: UploadRequestOption) => {
    onImport(file as File);

    onSuccess?.({});
  };

  return (
    <Flex vertical gap="middle">
      <Paragraph style={{ marginBottom: 0 }}>
        Import devices and their installed software from an Installed Software
        Report (.csv) into <Text strong>{userProfile.organization.name}</Text>.
        Each <Text code>Name</Text> is one device. Buildings are matched to{' '}
        <Text code>Site Name</Text> and created when they don&apos;t exist.
        Re-importing a device replaces its software list. Software and
        publishers go through the standard catalog normalization, so imported
        devices appear in the software vulnerability reports.
      </Paragraph>

      {status === 'failed' && (
        <Alert
          type="error"
          showIcon
          title={`${fileName} was not imported`}
          description={error}
        />
      )}

      <Spin
        spinning={busy}
        description={
          status === 'validating'
            ? 'Checking file format...'
            : `Importing ${fileName}. This can take a minute for large files...`
        }
      >
        <Upload.Dragger
          accept=".csv"
          multiple={false}
          showUploadList={false}
          disabled={busy}
          customRequest={handleUpload}
        >
          <p>
            <CloudUploadOutlined style={{ fontSize: 40 }} />
          </p>
          <p>Click or drag an Installed Software Report (.csv) here</p>
          <p>
            <Text type="secondary">One file at a time, up to 50 MB.</Text>
          </p>
        </Upload.Dragger>
      </Spin>

      <div>
        <Text type="secondary">Required columns: </Text>
        {REQUIRED_COLUMNS.map((column) => (
          <Tag key={column} color="blue">
            {column}
          </Tag>
        ))}
        <br />
        <Text type="secondary">Optional columns: </Text>
        {OPTIONAL_COLUMNS.map((column) => (
          <Tag key={column}>{column}</Tag>
        ))}
      </div>
    </Flex>
  );
};

export default SoftwareImportUpload;
